/**
 * What the billing page and banners show, as pure functions of the billing
 * state, so the rules are tested apart from the components.
 */
import type { DotColor } from "@/components/shared/DotChip";
import { daysLeft, scheduledCancel, type PlanSource, type SubscriptionRowLite } from "./orgPlan";
import { ENTITLING_STATUSES } from "../../../supabase/functions/_shared/stripe-billing";

/**
 * The subscription to show for a company: only rows of this deployment's mode
 * (test before launch, live after), an entitling one first.
 */
export function pickOrgSubscription(
  subs: SubscriptionRowLite[],
  live: boolean
): SubscriptionRowLite | null {
  const inMode = subs.filter((s) => s.livemode === live);
  return inMode.find((s) => ENTITLING_STATUSES.includes(s.status)) ?? inMode[0] ?? null;
}

export type ChipKind = "free" | "trial" | "active" | "pastDue" | "ending" | "none";

export interface PlanChip {
  kind: ChipKind;
  color?: DotColor;
  alert?: boolean;
  /** For `ending`: when the subscription stops. */
  date?: string;
}

export function planChip(
  plan: { source: PlanSource; status: string },
  sub: SubscriptionRowLite | null
): PlanChip {
  if (plan.source === "trial") return { kind: "trial", color: "blue" };
  if (plan.source === "free") return { kind: "free", color: "green" };
  if (plan.source === "subscription" && sub) {
    if (sub.status === "past_due") return { kind: "pastDue", color: "red", alert: true };
    const ends = scheduledCancel(sub);
    if (ends) return { kind: "ending", color: "amber", date: ends };
    return { kind: "active", color: "green" };
  }
  return { kind: "none" };
}

export type BannerKind = "trial" | "trialEnded" | "pastDue" | null;

/**
 * At most one banner in the app chrome, most urgent first:
 * a failed payment (everyone in the company sees it), then for the owner the
 * running trial with its days left, or a trial that ended without a plan.
 */
export function bannerFor(
  input: {
    plan: { source: PlanSource; status: string };
    orgSubscription: SubscriptionRowLite | null;
    /** The company's trial window. */
    orgTrial: { ends_at: string } | null;
    isOwner: boolean;
  },
  now: Date
): { kind: BannerKind; days?: number } {
  // Free access: nothing to pay, nothing to nag about.
  if (input.plan.source === "free") return { kind: null };
  if (input.orgSubscription?.status === "past_due") return { kind: "pastDue" };
  if (!input.isOwner) return { kind: null };
  if (input.plan.source === "trial" && input.orgTrial) {
    return { kind: "trial", days: daysLeft(input.orgTrial.ends_at, now) };
  }
  if (
    input.plan.source === "none" &&
    input.orgTrial &&
    daysLeft(input.orgTrial.ends_at, now) === 0
  ) {
    return { kind: "trialEnded" };
  }
  return { kind: null };
}

/**
 * Whether to call billing_start_trial() now: a company owner who never had a
 * trial or a subscription, once billing is live. A freshly signed-up person
 * owns the company handle_new_user created, so their 14 days start on the
 * first app page they open. Free access (grandfathered or comped) needs none.
 */
export function shouldStartTrial(input: {
  live: boolean;
  hasTrial: boolean;
  ownSubscriptions: number;
  ownedOrgs: number;
  planSource: PlanSource;
}): boolean {
  if (!input.live) return false;
  if (input.hasTrial || input.ownSubscriptions > 0 || input.ownedOrgs === 0) return false;
  return input.planSource !== "free";
}
